import { useState } from 'react'
import { useAppSelector } from '@/utils'
import { t } from '@lingui/macro'
import { DatePicker, InteractiveModal, Select } from '@/ui/kit'
import style from './style.module.scss'
import { DATE_FORMAT, formatDate } from '@/utils/lib/formatDate'
import { SVG } from '@/assets/svg'
import clsx from 'clsx'
import { FILTER_INITIAL_TIME, FILTER_INITIAL_TIME_END } from '@/core/constants'
import { useExchageType } from '@/utils/hooks/useExchangeType'
import { isExchangeBybit, isExchangeTigerX } from '@/utils/lib/exchange'
import { format } from 'date-fns'
import { options } from './options'

const now = new Date()
const end = new Date(now)
end.setHours(0, 0, 0, 0)

const start = new Date(now)
start.setDate(now.getDate() - 7)
start.setHours(0, 0, 0, 0)

const defaultTime = {
  startTimeMS: format(start, 'HH:mm:ss'), // "00:00:00"
  endTimeMS: format(end, 'HH:mm:ss'), // "00:00:00"
}

type Props = {
  onChange: (filters: any) => void
  initialFilters: any
}

export const HistoryBybitFilter = (props: Props) => {
  const [isOpen, setIsOpen] = useState(false)
  const [filterData, setFilterData] = useState<any>(props.initialFilters)
  const [timeData, setTimeData] = useState(defaultTime)

  const { exchangeType } = useExchageType()
  const isBybitFilter = isExchangeBybit(exchangeType)
  const isTigerXFilter = isExchangeTigerX(exchangeType)

  const handleChangeDateRangeAndTime = (start: string, end: string, startTime?: string, endTime?: string) => {
    const updatedFilters = {
      startTime: new Date(start).getTime(),
      endTime: new Date(end).getTime(),
      type: filterData.type,
      baseCoin: filterData.baseCoin,
      limit: filterData.limit,
    }

    setTimeData({
      startTimeMS: timeData.startTimeMS ?? FILTER_INITIAL_TIME,
      endTimeMS: timeData.endTimeMS ?? FILTER_INITIAL_TIME,
    })

    setFilterData(updatedFilters)
    props.onChange(updatedFilters)
    setIsOpen(false)
  }

  const handleResetDates = () => {
    setFilterData(props.initialFilters)
    props.onChange(props.initialFilters)
  }

  const updateFilterField = (key: keyof typeof filterData, value: string | undefined) => {
    const isSame = filterData[key] === value
    const updatedFilters = {
      ...filterData,
      [key]: isSame ? '' : value,
    }

    setFilterData(updatedFilters)
    props.onChange(updatedFilters)
  }

  const handleTypeHandler = (value: string) => {
    updateFilterField('type', value)
  }

  const handleCoinHandler = (value: string) => {
    updateFilterField('baseCoin', value)
  }

  ///////////////////////////////////////
  const { linearInstruments } = useAppSelector(state => state.overview)
  const coinOptions = linearInstruments
    ?.map(item => ({
      value: item.baseCoin,
      label: item.baseCoin,
    }))
    .filter((item, index, self) => self.findIndex(t => t.value === item.value) === index)

  return (
    <>
      <div className={clsx(style.filterContainer)}>
        <div className={style.filterContent}>
          <div className={style.filterName}>{t({ message: 'Date range', id: 'historyTrade.dateRange' })}</div>
          <div className={style.datePickerBlock} onClick={() => setIsOpen(true)}>
            <div className={style.date}>
              <span>{formatDate(filterData.startTime, DATE_FORMAT.DAY_AND_YEAR_SEMI)}</span>
              <span className={style.date}> {timeData.startTimeMS ? timeData.startTimeMS : FILTER_INITIAL_TIME}</span>
              <span className={style.line}>-</span>
              <span>{formatDate(filterData.endTime, DATE_FORMAT.DAY_AND_YEAR_SEMI)}</span>
              <span className={style.date}>{timeData.endTimeMS ? timeData.endTimeMS : FILTER_INITIAL_TIME}</span>
            </div>
            <div
              className={clsx(style.toggleChevron, {
                [style.open]: isOpen,
              })}
            >
              <SVG.Arrows.ArrowDown className={style.toggleChevron} />
            </div>
          </div>
        </div>
        <div className={style.select}>
          <span className={style.filterName}>{t({ message: 'Base Coin', id: 'historyTrade.baseCoin' })}</span>
          <Select
            placeholder={t({ message: 'Select Base Coin', id: 'historyTrade.baseCoinSelect' })}
            onChange={handleCoinHandler}
            size={Select.Size.Small}
            value={filterData.baseCoin}
            options={(coinOptions as any) ?? []}
            variant={Select.Variant.Raw}
            className={style.selectContent}
            withSearch
          />
        </div>
        <div className={style.select}>
          <span className={style.filterName}>{t({ message: 'type', id: 'historyTrade.type' })}</span>
          <Select
            placeholder={t({ message: `Select Type`, id: 'historyTrade.selectType' })}
            onChange={handleTypeHandler}
            size={Select.Size.Small}
            value={filterData.type}
            options={options}
            variant={Select.Variant.Raw}
            className={style.selectContent}
          />
        </div>

        <div onClick={handleResetDates} className={style.resetButton}>
          {t({ message: 'Reset', id: 'historyTrade.reset' })}
        </div>
      </div>
      <InteractiveModal isOpen={isOpen}>
        <DatePicker
          startDate={new Date(filterData.startTime)}
          endDate={new Date(filterData.endTime)}
          onApply={handleChangeDateRangeAndTime}
          onClose={() => setIsOpen(false)}
          isBybitHistoryFilter={isBybitFilter}
          isTigerXHistoryFilter={isTigerXFilter}
          timePickerData={{
            start: timeData.startTimeMS,
            end: timeData.endTimeMS,
          }}
        />
      </InteractiveModal>
    </>
  )
}
